import { supabase } from './supabase-config';

import { breadcrumb, log } from '@utils/logging';
import {
    Class,
    Division,
    DivisionToInsert,
    Event,
    EVENT_ROLE,
    EventMembership,
    EventRoleStatus,
    RegisteredEvent,
    STATUS,
    TEAM_ROLE,
} from '@type/db-types';
import invariant from 'tiny-invariant';
import { toast } from 'react-toastify';
import { getSignedInUser, spreadInnerArraySingle } from './supabase-utils';
import { EventForm } from '@components/eventCreation/CreateEventUtils';
import { groupBy, map } from 'lodash';
import { mapEventFormToEvent } from '@utils/event-utils';
import {
    RealtimePostgresInsertPayload,
    RealtimePostgresUpdatePayload,
} from '@supabase/supabase-js';
import { getTeamMembershipInfo } from './teams';
import { inviteUserToEvent, inviteUserToTeam } from '@modules/api';

const EVENTS = 'events';
const EVENT_MEMBERSHIP = 'user_event';
const DIVISIONS = 'divisions';
const CLASSES = 'classes';
const REGISTERED_EVENTS = 'RegisteredEvents';
const EVENT_ROLES = 'EventRoles';

export interface EventRoleWithMemberships extends EventRoleStatus {
    memberships: MembershipMetadata[];
}
interface MembershipMetadata {
    athleteIds: string[];
    names: string[];
    role: EVENT_ROLE;
    status: STATUS;
    count: number;
}

function combineMemberships(events: EventRoleStatus[]): EventRoleWithMemberships[] {
    const groupedByEvent = groupBy(events, 'id');
    const mapped = map(groupedByEvent, (membershipsForThisEvent: RegisteredEvent[]) => {
        const groupedMemberships = groupBy(
            membershipsForThisEvent,
            (m) => `${m.membershipStatus}-${m.membershipRole}`,
        );
        const memberships = map(groupedMemberships, (membershipsForThisRoleStatus) => {
            const { membershipRole, membershipStatus } = membershipsForThisRoleStatus[0];
            const initialValue: MembershipMetadata = {
                athleteIds: [],
                names: [],
                role: membershipRole as EVENT_ROLE,
                status: membershipStatus as STATUS,
                count: 0,
            };
            return membershipsForThisRoleStatus.reduce(
                (prev, { firstName, lastName, athleteId }) => {
                    const { athleteIds, names, role, status, count } = prev;
                    if (athleteIds.includes(athleteId)) {
                        return prev; // we can have dupes here when the same user is a coach and a parent to the same athlete, the status is already the same so we skip it
                    }
                    return {
                        athleteIds: [...athleteIds, athleteId],
                        names: [...names, `${firstName} ${lastName}`],
                        count: count + 1,
                        role,
                        status,
                    };
                },
                initialValue,
            );
        });
        const combinedMemberships: EventRoleWithMemberships = {
            ...membershipsForThisEvent[0],
            memberships,
        };

        return combinedMemberships;
    }) as unknown as EventRoleWithMemberships[];

    return mapped;
}

export const fetchRegisteredEvents = async (
    eventId?: string,
): Promise<EventRoleWithMemberships[]> => {
    const user = await getSignedInUser();
    invariant(user, 'User must be signed in to get registered events');

    let query = supabase.from(REGISTERED_EVENTS).select('*').eq('userId', user.id);

    // if eventId is passed, filter by eventId
    if (eventId) {
        query = query.eq('eventId', eventId);
    }
    const { data, error } = await query;

    // console.log(`fetchRegisteredEvents`, { data, error });

    if (error) {
        log(`[fetchRegisteredEvents] ${error.message}`, error);
    }

    const withMemberships = combineMemberships(data as EventRoleStatus[]);

    console.log(`registeredEvents`, withMemberships);
    return withMemberships;
};

export const fetchManageableEvents = async (): Promise<EventRoleStatus[]> => {
    const user = await getSignedInUser();
    invariant(user, 'User must be signed in to get manageable events');

    const { data, error } = await supabase
        .from(EVENT_ROLES)
        .select('*')
        .in('status', [STATUS.ACTIVE, STATUS.PENDING])
        .eq('userId', user.id)
        .in('membershipRole', [EVENT_ROLE.STAFF, EVENT_ROLE.OWNER]);

    // console.log(`fetchManageableEvents`, { data, error });

    if (error) {
        log(`[fetchManageableEvents] ${error.message}`, error);
    }
    return (data || []) as EventRoleStatus[];
};

export const fetchPublicEvents = async (): Promise<Event[]> => {
    const [{ data, error }, { data: eventCounts, error: countError }] = await Promise.all([
        supabase
            .from(EVENTS)
            .select(
                `*,
                    divisions: divisions(*, classes: classes(*))
        `,
            )
            .eq('status', STATUS.ACTIVE),
        supabase.from('RegistrantCount').select(`*`).eq('status', STATUS.ACTIVE),
    ]);

    if (error || countError) {
        log(`[fetchPublicEvents] ${error?.message || countError?.message}`, { error, countError });
    }

    return (data?.map((e) => ({
        ...e,
        memberships: [],
        registrantsCount:
            (eventCounts || []).find((c) => c.eventId === e.id)?.registrantsCount || 0,
    })) || []) as Event[];
};

// get single public event
export const fetchPublicEvent = async (eventId: string): Promise<EventWithMembership | null> => {
    const [{ data, error }, registrantsCount] = await Promise.all([
        supabase
            .from(EVENTS)
            .select(
                `
        *,
        divisions: divisions(*, classes: classes(*))
    `,
            )
            .eq('status', STATUS.ACTIVE)
            .eq('id', eventId)
            .single(),

        fetchEventRegistrationCount(eventId),
    ]);

    breadcrumb('fetchPublicEvent', { data, error });

    if (error) {
        log(`[fetchPublicEvents] ${error.message}`, error);
    }
    const returnData: EventWithMembership = {
        ...((data || {}) as Event),
        memberships: [],
        registrantsCount,
    };
    return returnData;
    // return data ? ({ ...data, registrantsCount } as Event & { registrantsCount: number }) : null;
};

export interface EventWithMembership extends Event {
    memberships: EventRoleWithMemberships['memberships'];
    membership?: Pick<EventMembership, 'status' | 'role' | 'classId'>;
    registrantsCount: number;
}

// need to get registrant count
// Note: you cannot join with memberships on the client side
// if you want to use View logic, you need to join on the server side
export const fetchEventsWithMembership = async (): Promise<EventRoleWithMemberships[]> => {
    const user = await getSignedInUser();
    invariant(user, 'User must be signed in to get events with membership');

    const [publicEvents, { data: eventsWithMembership, error }] = await Promise.all([
        fetchPublicEvents(),
        supabase
            .from(REGISTERED_EVENTS)
            .select('*')
            .eq('status', STATUS.ACTIVE)
            .eq('userId', user.id),
    ]);

    if (error) {
        log(`[fetchEventsWithMembership] ${error.message}`, error);
    }

    const withMembership = combineMemberships(eventsWithMembership as EventRoleStatus[]);
    const combinedEvents = publicEvents.map((e) => {
        const memberships = withMembership.find((m) => m.id === e.id)?.memberships || [];

        const event: EventRoleWithMemberships = { ...e, memberships };
        return event;
    });

    breadcrumb('fetchEventsWithMembership', { combinedEvents, error });

    return combinedEvents;
};

// get single event registration count
export const fetchEventRegistrationCount = async (eventId: string): Promise<number> => {
    const { data, error } = await supabase
        .from('RegistrantCount')
        .select(`registrantsCount`)
        .eq('eventId', eventId)
        .single();

    if (error) {
        log(`[fetchEventRegistrationCount] ${error.message}`, error);
    }
    breadcrumb('[fetchEventRegistrationCount]', { data, error });

    return data?.registrantsCount || 0;
};

// get single event with user's membership details
export const fetchEventWithMembership = async (
    userId: string,
    eventId: string,
): Promise<EventWithMembership | null> => {
    const [{ data: eventData, error: eventError }, otherMemberships, registrantsCount] =
        await Promise.all([
            supabase
                .from(EVENTS)
                .select(
                    `
            *,
            membership: user_event( status, role, classId, divisionId),
            divisions: divisions(*, classes: classes(*))
        `,
                )
                // FIXME: discuss, do we even want this filter?
                .in('status', [STATUS.ACTIVE, STATUS.PENDING, STATUS.INVITED])
                .eq('user_event.userId', userId)
                .eq('id', eventId)
                .single(),
            fetchRegisteredEvents(eventId),
            fetchEventRegistrationCount(eventId),
        ]);

    if (eventError || !eventData) {
        log(`[fetchEventWithMembership] ${eventError.message}`, eventError);
    }

    const combinedMemberships = combineMemberships((otherMemberships as EventRoleStatus[]) || []);
    const eventWithMembership: EventWithMembership = {
        ...((eventData || {}) as Event),
        memberships: combinedMemberships?.[0]?.memberships || [],
        registrantsCount,
    };

    const spreadData = spreadInnerArraySingle(
        { ...eventWithMembership } as EventWithMembership,
        (d) => d.membership as unknown as [], // this key is an array of one item, should just be an object...
    );

    breadcrumb('fetchEventWithMembership', {
        eventWithMembership,
        eventError,
        registrantsCount,
    });

    return spreadData[0] || null;
};

// get user's event membership details
export const getUserEventMembership = async (
    eventId: string,
    userId: string,
    expectData: boolean = false,
): Promise<EventMembership | null> => {
    invariant(userId && eventId, '[getUserEventMembership] userId & eventId required');
    const { data, error } = await supabase
        .from(EVENT_MEMBERSHIP)
        .select('*')
        .eq('eventId', eventId)
        .eq('userId', userId)
        .single();

    if (error) {
        if (expectData) {
            log(`[getUserEventMembership] ${error.message}`, error);
        } else {
            console.log(`[getUserEventMembership] ${error.message}`);
        }
    }
    return data || null;
};

export async function addUpdateEvent(
    event: EventForm | (Partial<EventForm> & { id: string }),
    description = 'addUpdateEvent',
): Promise<Event | null> {
    const isNewEvent = !event.id;

    breadcrumb('addUpdateEvent', { event, description });
    return toast
        .promise(
            async () => {
                breadcrumb('addUpdateEvent', { event, description });

                const { data: updatedEvent, error } = await supabase
                    .from(EVENTS)
                    .upsert(mapEventFormToEvent(event))
                    .select()
                    .single();

                if (error || !updatedEvent.id) {
                    log(`[addUpdateEvent]: ${error?.message}`, { error, description });
                    throw new Error(error?.message || 'Event ID missing');
                } else {
                    console.log(`(${description}) success `, updatedEvent);
                    const [updatedDivisions /* , adminMembership */] = await Promise.all([
                        // only update divisions if they exist?
                        event.divisions
                            ? updateEventDivisions(updatedEvent.id, event.divisions)
                            : Promise.resolve([]),
                        addEventMembership({
                            role: EVENT_ROLE.OWNER,
                            eventId: updatedEvent.id,
                            userId: updatedEvent.createdByUserId,
                            status: STATUS.ACTIVE,
                        }),
                    ]);

                    return {
                        ...updatedEvent,
                        divisions: updatedDivisions.filter((d) => d),
                    } as Event;
                }
            },
            {
                pending: `${isNewEvent ? 'Adding' : 'Updating'} event...`,
                success: `${isNewEvent ? 'Added' : 'Updated'} event`,
                error: `Failed to ${isNewEvent ? 'add' : 'update'} event`,
            },
        )
        .catch(() => null);
}

// update event's divisions
export const updateEventDivisions = async (
    eventId: string,
    divisions: (Division | DivisionToInsert)[],
) => Promise.all(divisions.map((d) => upsertDivision({ ...d, eventId } as Division)));

/**
 * Add, update, or remove divisions from an event
 * handle added/edited divisions
 * @param event with
 * @param description
 * @returns
 */
const upsertDivision = async ({ classes, ...division }: Division): Promise<Division> => {
    const { data: updatedDivision, error: updateDivisionError } = await supabase
        .from(DIVISIONS)
        .upsert(division)
        .eq('id', division.id)
        .select('*')
        .single();

    if (updateDivisionError) {
        log(`[upsertDivision] ${updateDivisionError.message}`, updateDivisionError);
        throw new Error(`[upsertDivision]: ${updateDivisionError.message}`);
    }

    const updatedClasses = await Promise.all(
        classes
            .filter((c) => {
                // validate that classes we're creating are "active"
                if (!c.id && c.status !== STATUS.ACTIVE) {
                    log(`[upsertDivision:Class] invalid class without id: ${c.status}`, c);
                    return false;
                }
                return true;
            })
            .map(async (c) => {
                const { data, error } = await supabase
                    .from(CLASSES)
                    .upsert({ ...c, divisionId: updatedDivision.id })
                    .select('*')
                    .single();

                if (error) {
                    breadcrumb(`[upsertDivision:Class] ${error.message}`, error);
                    return null;
                }

                return data || null;
            }),
    );

    if (updatedClasses.some((c) => !c)) {
        log(`[upsertDivision] failed to update classes`, updatedClasses);
        throw new Error(`[upsertDivision]:  failed to update classes`);
    }

    return { ...updatedDivision, classes: updatedClasses as Class[] };
};

export async function cancelEvent(id: string, description = 'cancelEvent'): Promise<Event | null> {
    return toast
        .promise(
            async () => {
                breadcrumb('cancelEvent', { id, description });

                const { data, error } = await supabase
                    .from(EVENTS)
                    .update({ status: STATUS.CANCELLED })
                    .eq('id', id);

                if (error) {
                    log(`[cancelEvent]: ${error.message}`, { error, description });
                    throw new Error(error.message);
                } else {
                    console.log(`(cancelEvent:${description}) success `, data);
                    return data;
                }
            },
            {
                pending: `Cancelling event...`,
                success: 'Event cancelled!',
                error: `Failed to cancel event`,
            },
        )
        .catch(() => null);
}

export function listenToRegisteredAthletesChanges(
    eventId: string,
    onMessage: () => void,
    statuses: STATUS[] = [STATUS.ACTIVE],
): () => void {
    const onCallback = async (
        payload:
            | RealtimePostgresInsertPayload<RegisteredEvent[]>
            | RealtimePostgresUpdatePayload<RegisteredEvent[]>,
    ) => {
        console.log(`listenToRegisteredAthletesChanges updated: `, payload);
        onMessage();
    };

    // Listen to inserts on the user table for the given userId
    const insertQuery = supabase
        .channel('listenToUserInsert')
        .on<RegisteredEvent[]>(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: EVENT_MEMBERSHIP,
                filter: `eventId=eq.${eventId},status=in.(${statuses.join(',')})`,
            },
            onCallback,
        )
        .subscribe();

    // Listen to updates on the user table for the given userId
    const updateQuery = supabase
        .channel('listenToUserUpdate')
        .on<RegisteredEvent[]>(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: EVENT_MEMBERSHIP,
                filter: `eventId=eq.${eventId},status=eq.${statuses.join(',')}`,
            },
            onCallback,
        )
        .subscribe();

    return () => {
        insertQuery.unsubscribe();
        updateQuery.unsubscribe();
    };
}

export const getAthletesForEvent = async (
    eventId: string,
    roles: EVENT_ROLE[] = [EVENT_ROLE.PARTICIPANT],
    statuses: STATUS[] = [STATUS.ACTIVE],
): Promise<RegisteredEvent[]> => {
    // FIXME: handle private events
    const { data, error } = await supabase
        .from(REGISTERED_EVENTS)
        .select(
            `*
            `,
        )
        .eq('eventId', eventId)
        .in('membershipStatus', statuses)
        .in('membershipRole', roles);

    if (error) {
        log(`[getAthletesForEvent] ${error.message}`, error);
    }

    // filters out parent/coach relationship data
    // couldn't get supabase to do this: .eq('userId', 'athleteId')
    const filteredData = data?.filter(({ userId, athleteId }) => userId === athleteId) || [];

    // change the ID to the athleteId to prevent table row ids from conflicting
    const athletesFromEvents = filteredData.map((e) => ({ ...e, id: e.athleteId }));

    breadcrumb('getAthletesForEvent', { athletesFromEvents, error });

    return (athletesFromEvents as RegisteredEvent[]) || [];
};

export function updateRegistrantClass(
    divisionId: string | null,
    classId: string | null,
    membershipId: string,
) {
    invariant(
        (classId || divisionId) && membershipId,
        '[updateRegistrantClass] membershipId and class or Division required',
    );
    // console.log(`updateRegistrantClass`, { divisionId, classId, membershipId });
    return supabase.from(EVENT_MEMBERSHIP).update({ classId, divisionId }).eq('id', membershipId);
}

export const getAdminsForEvent = async (eventId: string): Promise<EventRoleStatus[]> => {
    const { data, error } = await supabase
        .from(EVENT_ROLES)
        .select('*')
        .eq('eventId', eventId)
        .in('membershipRole', [EVENT_ROLE.STAFF, EVENT_ROLE.OWNER]);

    if (error) {
        log(`[getAdminsForEvent] ${error.message}`, error);
    }

    breadcrumb('getAdminsForEvent', { data, error });

    return (data as EventRoleStatus[]) || [];
};

// update event role
export const updateEventRole = async (role: EVENT_ROLE, membershipId: string) => {
    const { data, error } = await supabase
        .from(EVENT_MEMBERSHIP)
        .update({ role })
        .eq('id', membershipId)
        .select('*')
        .single();

    if (error) {
        log(`[updateEventRole] ${error.message}`, error);
    }

    breadcrumb('updateEventRole', { data, error });

    return data || null;
};

// update event role
export const addEventMembership = async (
    membership: Pick<EventMembership, 'eventId' | 'userId' | 'role' | 'status'>,
) => {
    const { data, error } = await supabase
        .from(EVENT_MEMBERSHIP)
        .insert(membership)
        .select('*')
        .single();

    if (error) {
        log(`[addEventMembership] ${error.message}`, error);
    }

    breadcrumb('addEventMembership', { data, error });

    return data || null;
};

export async function getEventStripeId(
    eventId: string,
): Promise<{ stripeId: string; userId: string }> {
    const { data, error } = await supabase
        .rpc('getEventStripeId', {
            eventId,
        })
        .single();

    breadcrumb('getEventStripeId', { data, error });

    if (error || !data) {
        log(`[getEventStripeId]: ${error?.message || 'event stripe account not found'}`, {
            data,
            error,
        });
        throw new Error(
            `[getEventStripeId]: ${error?.message || 'event stripe account not found'}`,
        );
    }

    return data;
}

export async function verifyPayToInviteStatus(
    userId: string,
    eventId: string,
    parentEntityTable: 'event' | 'team',
    email: string,
) {
    const isEvent = parentEntityTable === 'event';
    const membership = isEvent
        ? await getUserEventMembership(eventId, userId, false)
        : await getTeamMembershipInfo(eventId, userId, false);
    const firstName = email.split('@')[0];

    const invite = {
        eventId,
        userId,
        role: EVENT_ROLE.OWNER,
        firstName,
        lastName: '',
        email,
        teamStatus: STATUS.INVITED,
        membershipStatus: STATUS.INVITED,
    };

    const notActive = ![STATUS.INVITED, STATUS.ACTIVE].includes(membership?.status as STATUS);

    // invalid membership, send invite
    if (!membership || notActive || membership.role !== TEAM_ROLE.OWNER) {
        return isEvent
            ? inviteUserToEvent(invite)
            : inviteUserToTeam({ ...invite, teamId: eventId, role: TEAM_ROLE.OWNER });
    }

    // already active
    console.warn(`[verifyPayToInviteStatus] user already has active membership`);
}
